/**
 * Token Storage Utility
 * 
 * Gestionează storage-ul token-urilor în memoria RAM (in-memory storage)
 * pentru securitate sporită împotriva XSS attacks.
 * 
 * ⚠️ NOTĂ: Tokens se pierd la page refresh, utilizatorul trebuie să se logheze din nou.
 * 
 * 🔐 UPGRADE PATH (pentru future):
 * - Implement httpOnly cookies pe backend pentru refresh token
 * - Access token rămâne în memory
 * - Silent refresh la page load folosind httpOnly cookie
 */

// In-memory storage pentru tokens
// NICIODATĂ nu se persistă în localStorage sau sessionStorage
let memoryStorage: {
  accessToken: string | null;
  refreshToken: string | null;
} = {
  accessToken: null,
  refreshToken: null,
};

/**
 * Salvează token-urile în memoria RAM
 */
export const setTokens = (accessToken: string, refreshToken: string): void => {
  memoryStorage.accessToken = accessToken;
  memoryStorage.refreshToken = refreshToken;
  
  // CLEANUP: Elimină orice token vechi din localStorage dacă există
  // (pentru utilizatori care upgradeaza de la versiunea veche)
  try {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  } catch (e) {
    // Ignore - poate nu avem acces la localStorage
  }
};

/**
 * Returnează access token-ul din memoria RAM
 */
export const getAccessToken = (): string | null => {
  return memoryStorage.accessToken;
};

/**
 * Returnează refresh token-ul din memoria RAM
 */
export const getRefreshToken = (): string | null => {
  return memoryStorage.refreshToken;
};

/**
 * Șterge toate token-urile din memoria RAM
 */
export const clearTokens = (): void => {
  memoryStorage.accessToken = null;
  memoryStorage.refreshToken = null;
  
  // CLEANUP: Elimină și din localStorage dacă există
  try {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  } catch (e) {
    // Ignore
  }
};

/**
 * Verifică dacă avem tokens valide în memorie
 */
export const hasTokens = (): boolean => {
  return !!memoryStorage.accessToken && !!memoryStorage.refreshToken;
};

/**
 * FUTURE: Helper pentru a verifica dacă avem httpOnly cookie cu refresh token
 * (când implementăm upgrade-ul pe backend)
 */
export const hasRefreshTokenCookie = (): boolean => {
  // Pentru viitor: verifică dacă există cookie-ul httpOnly prin a face un request la backend
  // Deocamdată returnează false
  return false;
};

/**
 * CLEANUP FUNCTION: Șterge tokens vechi din localStorage pentru utilizatori existenți
 * Apelează aceasta la startup-ul aplicației
 */
export const cleanupOldStorage = (): void => {
  try {
    const oldAccessToken = localStorage.getItem('accessToken');
    const oldRefreshToken = localStorage.getItem('refreshToken');
    
    if (oldAccessToken || oldRefreshToken) {
      console.warn('⚠️ Found old tokens in localStorage. Cleaning up for security...');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      console.info('✅ Old tokens removed. Please login again.');
    }
  } catch (e) {
    // Ignore - poate nu avem acces la localStorage
  }
};
